import React, { PropTypes, Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import _flatMap from 'lodash/flatMap';
import WatchPropTypes from 'common/PropTypes';
import { contentSelect, displayModalMessage, sendContentReward } from 'common/actions/appActions';
import { roadblockSelector } from 'common/selectors/appSelector';
import { persistentFlagsSelector } from 'common/selectors/flagsSelector';
import playingStreamSelector from 'common/selectors/playingStreamSelector';
import {
    preauthorizedResourcesSelector,
    userLocationSelector,
    userAuthSelector,
    adobePassIsAuthorizedSelector
} from 'common/selectors/authenticationSelectors';
import pageSelector from 'common/selectors/pageSelector';
import { oneIdEntitlementsSelector } from 'common/selectors/oneIdSelector';
import pickerStreamsSelector from 'common/selectors/pickerStreamsSelector';
import {
    canPlayContent,
    contentContainsStream,
    isContentLocked,
    getContentId
} from 'lib/auth';
import { scrollTo } from 'lib/ui';
import { isEmpty } from 'lib/util';
import { getPlayerUrl, openStreamPicker } from 'lib/streamPicker';
import { queryString } from 'lib/format';
import { BucketLayout, ModalType, ContentType, ContentStatus, URLPath, Flags } from 'enums';
import globalVideoElement from 'webapp/player/globalVideoElement';

export default (WrappedComponent) => {
    class Playable extends Component {
        constructor(props) {
            super(props);
            this.blackoutClickHandler = this.blackoutClickHandler.bind(this);
            this.clickHandler = this.clickHandler.bind(this);
            this.isUserBlackedOut = this.isUserBlackedOut.bind(this);
        }

        /**
         * Generates a URL for content that does not go to the player page when clicked
         * @param {string} page - show|film|product
         * @param {boolean} seo - whether or not to put descriptive "junk" on the end of the url
         * @param {boolean} toURL - used in render function for right click -> open in new tab
         */
        getContentPageUrl(page, seo = false, toURL = false) {
            const {
                content: { id, name, type, suggestion },
                flags,
                routing
            } = this.props;

            const { basename } = (routing && routing.locationBeforeTransitions) || {};
            let junk = '';

            if (seo && typeof name === 'string') {
                const currentName = name.trim().toLowerCase();
                // catching edge case where URL ends with "espn+"
                if (currentName === 'espn+') {
                    junk = `/${currentName}`;
                } else {
                    // replace all special characters to include spaces with a "-"
                    junk = `/${currentName.replace(/[^A-Z0-9]+/ig, '-')}`;
                }
            }

            // TODO: DTC - API to return `links.web`
            let pathname = '';
            if (ContentType.TERM === type) {
                pathname = `/${page}/${encodeURIComponent(suggestion)}`;
            } else {
                pathname = `/${page}/${id}${junk}`;
            }
            const query = {
                ...flags
            };

            if (toURL) {
                return `${basename}${pathname}${queryString(query)}`;
            }

            return {
                pathname,
                query
            };
        }

        isUserBlackedOut() {
            const { listing, userLocation, content: { streams = [] } = {} } = this.props;

            // do not label the content as `blackout` unless all the streams are blacked out
            if (streams.length && streams.every(stream => !!stream.blackoutText)) {
                return true;
            }

            // This logic is for schedule link only
            // FIXME: currently GraphQL doesn't support blackout info (it used to work when using V1 listings)
            const { blackouts = [] } = listing;
            let userIsBlackedOut = false;
            if (blackouts.length > 0) {
                // Return true if we don't have userLocation - this check is only for UI purposes
                if (!userLocation) {
                    return true;
                }
                const { dma = '' } = userLocation || {};
                const dmaBlackout = blackouts.filter(({ type }) => (type === 'dma')).shift();
                userIsBlackedOut = dmaBlackout && dmaBlackout.detail.indexOf(dma.toString()) >= 0;
            }
            return userIsBlackedOut;
        }

        blackoutClickHandler(event) {
            event.preventDefault();
            this.props.dispatch(displayModalMessage(ModalType.BLACKOUT_MAP, { content: this.props.content }));
        }

        clickHandler(event) {
            if (event) {
                event.preventDefault();
            }
            const {
                bucketId,
                content,
                playingStream,
                dispatch,
                groupId,
                flags,
                isHero,
                isMobile,
                placement,
                bucketName,
                pageId,
                pageName,
                roadblock,
                router,
                routing = {},
                userAuth,
                entitlements,
                preauthorizedResources,
                country,
                lang,
                pickerStreams = [],
                isAuthorized
            } = this.props;

            const { pathname = '', query = {} } = (routing && routing.locationBeforeTransitions) || {};

            if (isEmpty(playingStream)) {
                globalVideoElement.prime();
            }

            if (roadblock === true && content.type === ContentType.LISTING) {
                const contentId = getContentId(content);
                // NOTE: Obtain the `ESPN_PLUS_*` packages as a flattened array
                const packages = (
                    content.streams &&
                    _flatMap(content.streams, stream => stream.packages)
                    .filter(result => result) // Removes undefined values from the array
                ) || [];
                const isPurchasable = packages.length > 0;
                const isEntitled = packages.some(pkg => entitlements.indexOf(pkg) !== -1);

                let roadblockUrl = `/watch/roadblock${queryString(contentId)}`;
                if (query[Flags.ROADBLOCK_NO_UNIVERSAL_LINK] !== 'true' && !(isPurchasable && !isEntitled)) {
                    // m.espn.com is enabled for Universal Link treatment on iOS, so use this domain
                    roadblockUrl = `//m.espn.com${roadblockUrl}`;
                }
                window.location = roadblockUrl;
                return;
            }

            if (
                [
                    ContentType.PRODUCT,
                    ContentType.SHOW,
                    ContentType.FILM,
                    ContentType.PROGRAM,
                    ContentType.TERM,
                    ContentType.COMPILATION
                ].indexOf(content.type) !== -1
            ) {
                const contentPageUrl = this.getContentPageUrl(URLPath[content.type], true);
                router.push(contentPageUrl);
                return;
            }

            const enhancedContent = !isEmpty(content) ? {
                ...content,
                bucketId,
                groupId,
                isHero,
                placement,
                bucketName,
                pageId,
                pageName
            } : content;

            const onSyndicatedPlayerPage = pathname.indexOf('/syndicated/player') !== -1;
            const canPlay = canPlayContent(enhancedContent, userAuth, false);
            const isESPNPlus = enhancedContent.streams.some(({ source: { id, name } }) => (
                name === 'ESPN+' || ['ESPN_DTC', 'ESPN_PPV'].indexOf(id) > -1
            ));
            const {
                dss: {
                    rewardToken = null,
                    contentId: dssContentId = null,
                    recommendedContentIds = null
                } = {}
            } = enhancedContent;

            if (rewardToken && dssContentId && recommendedContentIds) {
                dispatch(sendContentReward({
                    rewardToken,
                    contentId: dssContentId,
                    recommendedContentIds
                }));
            }

            const syndicatedScheduleLink = (onSyndicatedPlayerPage && placement.indexOf('schedule') > -1);
            const syndicatedBigPlayButton = (onSyndicatedPlayerPage && !syndicatedScheduleLink);
            const playerUrlQueryProps = { bucketId, groupId, flags, routing };

            // Allow linear/TVE to display in-player provider picker, but open ESPN+/DTC in a new tab
            if (!canPlay && (syndicatedScheduleLink || (syndicatedBigPlayButton && isESPNPlus))) {
                // Force content to open in a new browser window/tab, minus the `/syndicated` path segment
                let target = '_blank';

                // Except for ESPN+ content embedded on a domain matching `/espn/`
                if ((syndicatedBigPlayButton && isESPNPlus) &&
                    window.frameElement &&
                    window.location.origin.match(/espn/)) {
                    target = '_parent';
                }

                const playerUrl = getPlayerUrl({
                    content: enhancedContent,
                    toURL: true,
                    absoluteUrl: true,
                    ...playerUrlQueryProps
                });
                window.open(playerUrl, target);
                return;
            }

            if (syndicatedScheduleLink || canPlay) {
                const onPlayerPage = !syndicatedScheduleLink && (pathname.indexOf('/player') !== -1);
                // if you're not in a Hero or on the player or syndicated player page, route to it
                if (!onPlayerPage && !syndicatedBigPlayButton) {
                    const urlParams = {
                        isHero,
                        content: enhancedContent,
                        onSyndicatedPlayerPage,
                        ...playerUrlQueryProps
                    };
                    const playerUrl = getPlayerUrl(urlParams);
                    if (playerUrl) {
                        // if a user selects upcoming content, direct to the player page with the upcoming alert badge
                        const alertContent = (enhancedContent.status === ContentStatus.UPCOMING) ? enhancedContent : null;
                        const rState = { alertContent, placement, bucketId, bucketName, pageId, pageName, userInitiated: true };
                        const { links: { picker } = {} } = enhancedContent;
                        const showPicker = picker !== undefined;
                        /**
                         * if user can play the content, we don't want to send it to the player right away; otherwise
                         * player will pick the first stream and play it to the user.
                         * We will show picker if links.picker is defined
                         */
                        if (showPicker) {
                            enhancedContent.isOnTile = true;
                            dispatch(contentSelect(enhancedContent));
                            openStreamPicker({
                                lang,
                                country,
                                dispatch,
                                pickerStreams,
                                content: enhancedContent,
                                urlParams,
                                route: {
                                    router,
                                    rState,
                                    locationBeforeTransitions: routing.locationBeforeTransitions
                                },
                                authRes: { userAuth, preauthorizedResources }
                            });
                        } else if (isHero && isAuthorized === false && pickerStreams.length) {
                            /**
                             * there are flaws how adobe authenticated user currently. User logs in with an authrized
                             * MVPD account, it assumes user can play selected content, but it doesn't know
                             * whether or not user can play single stream until doing another authentication check.
                             * We need to handle usecase when user is authenticated and clicks on video hero compenent
                             * and user is not isAuthorized to place selected stream, we will reload the page, so
                             * the auth Z error will be handled in connectPlayer.js
                             */
                            window.location.reload();
                        } else {
                            // we may need to handle use case here when links.picker is not defined and if it is multi streams
                            router.push({
                                ...playerUrl,
                                state: rState
                            });
                        }
                        return;
                    }
                } else if (isMobile === true && onPlayerPage) {
                    scrollTo(document.body, 0, 250);
                }
            }

            dispatch(contentSelect(enhancedContent));
        }

        render() {
            const {
                content,
                playingStream,
                placement,
                bucketName,
                pageId,
                pageName,
                userAuth,
                preauthorizedResources
            } = this.props;

            const userAuthenticatedForContent = canPlayContent(content, userAuth);

            const showLock = isContentLocked(content, userAuth, preauthorizedResources);

            let playerHref;
            if (
                [
                    ContentType.PRODUCT,
                    ContentType.SHOW,
                    ContentType.FILM,
                    ContentType.PROGRAM,
                    ContentType.TERM,
                    ContentType.COMPILATION
                ].indexOf(content.type) !== -1
            ) {
                playerHref = this.getContentPageUrl(URLPath[content.type], true, true) || '#';
            } else {
                const {
                    bucketId,
                    groupId,
                    flags,
                    routing
                } = this.props;

                playerHref = getPlayerUrl({
                    content,
                    toURL: true,
                    bucketId,
                    groupId,
                    flags,
                    routing
                }) || '#';
            }

            const userIsBlackedOut = this.isUserBlackedOut();

            const newProps = {
                ...this.props,
                playerHref,
                content: !isEmpty(content) ? { ...content, placement, bucketName, pageId, pageName } : content,
                selected: contentContainsStream(content, playingStream),
                userAuthenticatedForContent,
                userIsBlackedOut,
                showLock
            };
            return (
                <WrappedComponent
                    {...newProps}
                    blackoutClickHandler={this.blackoutClickHandler}
                    clickHandler={this.clickHandler}
                />
            );
        }
    }

    Playable.defaultProps = {
        featured: false,
        isMobile: false,
        layoutType: BucketLayout.DEFAULT,
        placement: 'unknown',
        bucketName: undefined,
        pageId: undefined,
        pageName: undefined,
        playingStream: {},
        listing: {},
        content: {},
        entitlements: []
    };

    Playable.propTypes = {
        bucketId: PropTypes.number,
        content: WatchPropTypes.content,
        listing: PropTypes.object,
        dispatch: PropTypes.func,
        groupId: PropTypes.number,
        isHero: PropTypes.bool,
        isMobile: PropTypes.bool,
        entitlements: PropTypes.array,
        flags: PropTypes.object,
        placement: PropTypes.string,
        bucketName: PropTypes.string,
        pageId: PropTypes.string,
        pageName: PropTypes.string,
        playingStream: WatchPropTypes.stream,
        roadblock: PropTypes.bool,
        routing: PropTypes.object,
        router: PropTypes.object,
        userAuth: PropTypes.array,
        userLocation: PropTypes.object,
        preauthorizedResources: PropTypes.array,
        lang: PropTypes.string,
        country: PropTypes.string,
        pickerStreams: PropTypes.array,
        isAuthorized: PropTypes.bool
    };

    const mapStateToProps = (state) => {
        const { routing } = state;
        const { id: pageId, name: pageName } = pageSelector(state);
        const { lang, country, isMobile } = state.app;
        return {
            routing,
            lang,
            isMobile,
            pageId,
            pageName,
            country,
            entitlements: oneIdEntitlementsSelector(state),
            flags: persistentFlagsSelector(state),
            playingStream: playingStreamSelector(state),
            preauthorizedResources: preauthorizedResourcesSelector(state),
            roadblock: roadblockSelector(state),
            userAuth: userAuthSelector(state),
            userLocation: userLocationSelector(state),
            pickerStreams: pickerStreamsSelector(state),
            isAuthorized: adobePassIsAuthorizedSelector(state)
        };
    };

    return withRouter(connect(mapStateToProps)(Playable), { withRef: true });
};



// WEBPACK FOOTER //
// ./app/webapp/common/playable.js